// 사이드카(/api/tpt/*) 호출에 인증 헤더 자동 첨부 — 글로벌 fetch 인터셉터.
//
// 배경 (2026-08-18 권한 경계 감사): 사이드카 라우트 상당수가 서버측 인증 가드 없이 동작했다.
//   그대로 가드를 붙이면 정상 사용자가 401 을 맞는데, 원인은 **클라이언트가 인증 헤더를 아예
//   보내지 않는 호출부가 30여 곳**이었기 때문이다(/project · /poll · /status · /reads ·
//   /room-memo · /scheduled-msgs · /sidebar-folders · /rc/find · /announce/ensure 등).
//   호출부를 하나씩 고치면 누락이 생기고 새 호출부가 또 같은 실수를 반복하므로,
//   여기서 한 번에 X-Auth-Token/X-User-Id 를 실어 서버가 신원을 검증할 수 있게 한다.
//   = 서버 가드 부착의 **선행 조건**. 이 빌드가 배포된 뒤에야 서버를 fail-closed 로 바꿀 수 있다.
//
// 규칙:
//   - 이미 해당 헤더를 설정한 호출은 건드리지 않는다(호출부 의도 우선, 덮어쓰기 금지).
//   - 비로그인 상태면 무동작 — 공개 라우트(/drive/public/{token} · /releases 등)는 그대로 동작.
//   - Request 객체 입력은 헤더 병합 시 body 스트림 재구성이 필요해 위험하므로 통과시킨다
//     (현 코드베이스의 /api/tpt 호출은 전부 string URL — 2026-08-18 전수 확인).
//   - URL 매칭은 '/api/tpt/' 포함 여부. 데스크톱(serverProxy)이 절대 URL 로 바꾼 뒤여도 동일하게 걸린다.
//
// CORS: 사이드카 middleware 가 /api/tpt 전역에서 요청 헤더를 그대로 Allow-Headers 로 echo 하므로
//   데스크톱(cross-origin, tauri.localhost)에서도 preflight 가 통과한다.
//
// 설치: main.tsx 에서 1회 (installServerProxy · installSafetyValidator 와 같은 계층).
import { useAuthStore } from '../stores/authStore';

const TPT_PATH = '/api/tpt/';

let installed = false;

export function installTptAuthFetch(): void {
	if (installed) return;
	installed = true;
	const original = window.fetch.bind(window);
	window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
		try {
			// Request 객체는 통과 (body 재구성 위험)
			if (typeof input !== 'string' && !(input instanceof URL)) return original(input, init);
			const url = typeof input === 'string' ? input : input.toString();
			if (!url.includes(TPT_PATH)) return original(input, init);
			const { authToken, userId } = useAuthStore.getState();
			if (!authToken || !userId) return original(input, init);
			const h = new Headers(init?.headers || {});
			if (!h.has('X-Auth-Token')) h.set('X-Auth-Token', authToken);
			if (!h.has('X-User-Id')) h.set('X-User-Id', userId);
			return original(input, { ...init, headers: h });
		} catch {
			return original(input as RequestInfo, init); // 어떤 이유로든 실패하면 원본 그대로 (가용성 우선)
		}
	}) as typeof window.fetch;
}
